import { usePwaStore } from '~~/stores/pwaStore'
import { isOwnPushSubscription, segmentifyOwnsPushOnBrand } from '~~/shared/constants/segmentifyPush'

type PushAskContext = 'add-to-cart' | 'login' | 'register'

export function usePwa() {
  const store = usePwaStore()
  // Na sklepach, gdzie push należy do Segmentify, własny kanał VAPID jest martwy:
  // o zgodę prosi ich kampania PUSH_PERMISSION, a jedyną subskrypcję `pushManager`
  // (jeden scope = jedna subskrypcja) trzyma ich SW. Patrz shared/constants/segmentifyPush.
  const segmentifyPush = segmentifyOwnsPushOnBrand(
    useRuntimeConfig().public.brand as string,
  )

  function detectStandalone(): boolean {
    if (typeof window === 'undefined') return false
    return (
      window.matchMedia?.('(display-mode: standalone)').matches ?? false
    ) || ((window.navigator as { standalone?: boolean }).standalone === true)
  }

  function isIosSafari(): boolean {
    if (typeof window === 'undefined') return false
    const ua = navigator.userAgent
    return /iPad|iPhone|iPod/.test(ua)
      && !(window as { MSStream?: unknown }).MSStream
      && /Safari/.test(ua)
      && !/CriOS|FxiOS|EdgiOS/.test(ua)
  }

  /** Lancôme rules: standalone? skip. cooldown? skip. visit<2? skip. Otherwise true. */
  function shouldShowInstallPrompt(): boolean {
    if (store.isStandalone) return false
    if (store.installPromptDismissedUntil && Date.now() < store.installPromptDismissedUntil) return false
    if (store.visitCount < 2) return false
    if (!store.canInstall && !isIosSafari()) return false
    return true
  }

  /**
   * Czy w ogóle da się zaoferować instalację (dla stałego przycisku).
   * Android/Chrome: mamy złapany beforeinstallprompt. iOS Safari: instrukcje
   * ręczne (brak beforeinstallprompt). Pomija już zainstalowane (standalone).
   * NIE sprawdza cooldownu — przycisk działa zawsze, gdy instalacja możliwa.
   */
  function canOfferInstall(): boolean {
    if (store.isStandalone) return false
    return store.canInstall || isIosSafari()
  }

  /**
   * Prompt po zakupie (strona potwierdzenia) — moment wysokiej intencji.
   * Świadomie POMIJA próg `visitCount >= 2` (kupujący jest zaangażowany nawet
   * przy pierwszej wizycie), ale respektuje standalone, cooldown i możliwość
   * instalacji (Android beforeinstallprompt lub iOS Safari).
   */
  function shouldOfferInstallAfterPurchase(): boolean {
    if (store.isStandalone) return false
    if (store.installPromptDismissedUntil && Date.now() < store.installPromptDismissedUntil) return false
    return store.canInstall || isIosSafari()
  }

  function shouldShowPushAsk(context: PushAskContext): boolean {
    // Własny soft-ask konkurowałby z kampanią PUSH_PERMISSION Segmentify o tę samą
    // zgodę przeglądarki — a zgodę można wyświetlić raz. Milczymy.
    if (segmentifyPush) return false
    if (store.pushPermission !== 'default') return false
    if (store.pushSoftAskDismissedUntil && Date.now() < store.pushSoftAskDismissedUntil) return false
    if (typeof window === 'undefined') return false
    if (!('Notification' in window) || !('PushManager' in window)) return false
    return ['add-to-cart', 'login', 'register'].includes(context)
  }

  async function triggerInstall(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
    const evt = store.installPromptDeferred
    if (!evt) return 'unavailable'
    await evt.prompt()
    const choice = await evt.userChoice
    store.installPromptDeferred = null
    store.canInstall = false
    return choice.outcome
  }

  async function requestPushAndSubscribe(consent?: { cart: boolean, promo: boolean, order: boolean }): Promise<'granted' | 'denied' | 'error'> {
    if (typeof window === 'undefined') return 'error'
    // Nasze `subscribe()` z własnym kluczem VAPID odebrałoby Segmentify jedyną
    // dostępną subskrypcję (albo dostało `InvalidStateError`, gdy zdążyli pierwsi).
    if (segmentifyPush) return 'error'
    if (!('Notification' in window) || !store.swRegistration) return 'error'

    const permission = await Notification.requestPermission()
    store.pushPermission = permission as 'granted' | 'denied'
    if (permission !== 'granted') return permission as 'denied'

    const config = useRuntimeConfig()
    const vapidKey = config.public.pwaVapidPublicKey
    if (!vapidKey) return 'error'

    // pushManager.subscribe() rzuca przy nieprawidłowym kluczu VAPID lub błędzie
    // usługi push — łapiemy to razem z błędem $fetch, żeby nigdy nie wypuścić
    // rejected promise do wywołującego (inaczej modal soft-ask zostaje wiszący).
    try {
      const subscription = await store.swRegistration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidKey),
      })

      await $fetch('/api/store/push/subscribe', {
        method: 'POST',
        body: {
          endpoint: subscription.endpoint,
          keys: subscription.toJSON().keys,
          consent: consent ?? { cart: true, promo: true, order: true },
        },
      })
      store.pushSubscriptionEndpoint = subscription.endpoint
      return 'granted'
    }
    catch (err) {
      console.error('[pwa] push subscribe failed', err)
      return 'error'
    }
  }

  async function unsubscribePush(): Promise<void> {
    const reg = store.swRegistration
    if (!reg) return
    const sub = await reg.pushManager.getSubscription()
    if (sub) {
      try {
        await $fetch('/api/store/push/subscribe', {
          method: 'DELETE',
          body: { endpoint: sub.endpoint },
        })
      }
      catch (err) {
        console.error('[pwa] push unsubscribe failed', err)
      }
      await sub.unsubscribe()
    }
    store.pushSubscriptionEndpoint = null
  }

  /**
   * Zwolnienie kanału push dla Segmentify: kasuje zastaną subskrypcję WYŁĄCZNIE, gdy
   * założyliśmy ją my (naszym kluczem VAPID). Subskrypcja Segmentify siedzi w TEJ SAMEJ
   * rejestracji `/sw.js` (jeden scope), więc samo `unsubscribePush()` przy każdym
   * starcie strony kasowało też ją — nikt nie zostawał zapisany na ich pushe.
   */
  async function releaseOwnPushForSegmentify(): Promise<void> {
    const reg = store.swRegistration
    if (!reg) return
    const sub = await reg.pushManager.getSubscription()
    if (!sub || !isOwnPushSubscription(sub.options.applicationServerKey)) return
    await unsubscribePush()
  }

  function trackVisit(): void {
    const now = Date.now()
    if (!store.firstVisitAt) store.firstVisitAt = now
    // New visit if > 30 min since last
    if (!store.lastVisitAt || now - store.lastVisitAt > 30 * 60 * 1000) {
      store.visitCount++
    }
    store.lastVisitAt = now
  }

  return {
    detectStandalone,
    isIosSafari,
    shouldShowInstallPrompt,
    canOfferInstall,
    shouldOfferInstallAfterPurchase,
    triggerInstall,
    shouldShowPushAsk,
    requestPushAndSubscribe,
    unsubscribePush,
    releaseOwnPushForSegmentify,
    trackVisit,
  }
}

function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4)
  const safe = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/')
  const raw = atob(safe)
  const out = new Uint8Array(raw.length)
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i)
  return out
}
